"use client";

import { useCallback } from "react";
import type {
  ConfirmThoughtsResponse,
  DeleteThoughtsResponse,
  IngestThoughtsResponse,
  MeiliIndex,
  MindstringPrototype,
} from "@/generated/fastapi";
import { useMeiliRefreshState, useUiRefreshState } from "@/state/globalStore";

export default function useThoughtRefresh() {
  const { meiliRefreshStamp, incrementMeiliRefreshStamp } =
    useMeiliRefreshState();
  const { uiRefreshStamp, incrementUiRefreshStamp } = useUiRefreshState();

  const isRefreshableStatus = useCallback(
    (status?: string | null) =>
      status === "success" || status === "created" || status === "pending",
    [],
  );

  const refreshStudioThoughts = useCallback(
    (prototypes?: MindstringPrototype | MindstringPrototype[]) => {
      const normalized = prototypes
        ? Array.isArray(prototypes)
          ? prototypes
          : [prototypes]
        : [];

      if (normalized.length === 0) {
        incrementMeiliRefreshStamp("studio_thoughts");
        return;
      }

      for (const prototype of normalized) {
        incrementMeiliRefreshStamp("studio_thoughts", prototype);
      }
    },
    [incrementMeiliRefreshStamp],
  );

  const refreshSuggestedThoughts = useCallback(
    (prototypes?: MindstringPrototype | MindstringPrototype[]) => {
      const normalized = prototypes
        ? Array.isArray(prototypes)
          ? prototypes
          : [prototypes]
        : [];

      if (normalized.length === 0) {
        incrementMeiliRefreshStamp("studio_suggested_thoughts");
        return;
      }

      for (const prototype of normalized) {
        incrementMeiliRefreshStamp("studio_suggested_thoughts", prototype);
      }
    },
    [incrementMeiliRefreshStamp],
  );

  const refreshReflectionSurfaces = useCallback(() => {
    incrementUiRefreshStamp("reset_reflections");
  }, [incrementUiRefreshStamp]);

  const syncIngestedThoughts = useCallback(
    ({
      response,
      shouldConfirm,
      prototype,
    }: {
      response: IngestThoughtsResponse | null | undefined;
      shouldConfirm: boolean;
      prototype?: MindstringPrototype;
    }) => {
      if (!response || !isRefreshableStatus(response.status)) {
        return;
      }

      if (shouldConfirm) {
        refreshStudioThoughts(prototype);
        return;
      }

      refreshSuggestedThoughts(prototype);
    },
    [isRefreshableStatus, refreshStudioThoughts, refreshSuggestedThoughts],
  );

  const syncDeletedThoughts = useCallback(
    ({
      response,
      indexName,
      prototypes,
    }: {
      response: DeleteThoughtsResponse | null | undefined;
      indexName: MeiliIndex;
      prototypes?: MindstringPrototype[];
    }) => {
      if (!response || !isRefreshableStatus(response.status)) {
        return;
      }

      if (indexName === "thoughts") {
        refreshStudioThoughts(prototypes);
        return;
      }

      refreshSuggestedThoughts(prototypes);
    },
    [isRefreshableStatus, refreshStudioThoughts, refreshSuggestedThoughts],
  );

  const syncSuggestedThoughtConfirmation = useCallback(
    (
      response: ConfirmThoughtsResponse | null | undefined,
      prototypes?: MindstringPrototype[],
    ) => {
      if (!response || !isRefreshableStatus(response.status)) {
        return;
      }

      refreshSuggestedThoughts(prototypes);
      refreshStudioThoughts(prototypes);
    },
    [isRefreshableStatus, refreshStudioThoughts, refreshSuggestedThoughts],
  );

  const syncSuggestedThoughtMutation = useCallback(
    (prototypes?: MindstringPrototype | MindstringPrototype[]) => {
      refreshSuggestedThoughts(prototypes);
    },
    [refreshSuggestedThoughts],
  );

  const syncSuggestedThoughtConfirmationViews = useCallback(
    (prototypes?: MindstringPrototype | MindstringPrototype[]) => {
      refreshSuggestedThoughts(prototypes);
      refreshStudioThoughts(prototypes);
    },
    [refreshStudioThoughts, refreshSuggestedThoughts],
  );

  const syncConfirmedThoughtMutation = useCallback(
    (prototypes?: MindstringPrototype | MindstringPrototype[]) => {
      refreshStudioThoughts(prototypes);
    },
    [refreshStudioThoughts],
  );

  return {
    refreshStudioThoughts,
    refreshSuggestedThoughts,
    refreshReflectionSurfaces,
    syncIngestedThoughts,
    syncDeletedThoughts,
    syncSuggestedThoughtConfirmation,
    syncSuggestedThoughtMutation,
    syncSuggestedThoughtConfirmationViews,
    syncConfirmedThoughtMutation,
    resetReflectionsRefreshKey: uiRefreshStamp["reset_reflections"],
    studioThoughtsRefreshStamp: meiliRefreshStamp["studio_thoughts"],
    studioSuggestedThoughtsRefreshStamp: meiliRefreshStamp["studio_suggested_thoughts"],
  };
}
